<template>
    <div class="chart-gauge" ref="chartContainer">

    </div>
</template>

<script>
    import Vue from 'vue'
    import * as d3 from 'd3'
    import Angles from '@/plugins/Angles'
    import hrNumber from '@/plugins/HumanReadableNumbers'
    import SvgHelper from '@/mixins/SvgHelper.js'

    export default {
        props: ['content', 'label'],
        mixins: [SvgHelper],
        name: 'gauge',
        data () {
            return {
                d3: d3,
                components: {},
                domain: null,
                range: [-Math.PI / 2, Math.PI / 2],
                normalizeScale: true,
                scaleExponent: 1,
                scaleSegments: [],
                shadedZones: null,
                subScales: [],
                value: null,
                colors: {
                    backgroundFill: '#EEEEEE',
                    stroke: '#AAAAAA',
                    text: '#333333',
                    label: '#009900'
                }
            }
        },
        computed: {
            hasContent () {
                return this.content !== undefined && this.content !== null && typeof this.content === 'object'
            }
        },
        methods: {
            _scale () {
                return this.components.scale
            },
            _colorScale () {
                return this.components.colorScale
            },
            _tooltip () {
                this.components.tooltip = this.components.tooltip || this._container().append('div')
                        .attr('class', 'tooltip')
                        .style('opacity', 0)

                return this.components.tooltip
            },
            _container () {
                this.components.container = this.components.container || this.d3.select(this.$refs.chartContainer)

                return this.components.container
            },
            _gauge (forceNew) {
                const container = this._container()

                if (forceNew) {
                    container.select('svg').remove()
                    this.components.gauge = null
                    this.components.groups = {}
                }

                this.components.gauge = this.components.gauge || container.append('svg')
                    .attrs({
                        width: this.geometry.width + 'px',
                        height: this.geometry.height + 'px',
                        viewbox: '0 0 ' + this.geometry.width + ' ' + this.geometry.height
                    })
                    .style('font-size', Math.ceil(this.geometry.radius / 6) + 'px')
                    .append('g')
                    .attr('transform', 'translate(' + (this.geometry.radius + this.geometry.padding.left) + ',' + (this.geometry.radius + this.geometry.padding.top) + ')')

                return this.components.gauge
            },
            _axis () {
                this.components.groups.axis = this.components.groups.axis || this._gauge().append('g').attr('class', 'axis')

                return this.components.groups.axis
            },
            _ticks () {
                this.components.groups.ticks = this.components.groups.axis || this._gauge().append('g').attr('class', 'axis ticks')

                return this.components.groups.ticks
            },
            _axisLabels () {
                this.components.groups.axisLabels = this.components.groups.axisLabels || this._gauge().append('g').attr('class', 'axis labels')

                return this.components.groups.axisLabels
            },
            _shadedZones () {
                this.components.groups.shadedZones = this.components.groups.shadedZones || this._gauge().append('g').attr('class', 'shaded-zones')

                return this.components.groups.shadedZones
            },
            _markers () {
                this.components.groups.markers = this.components.groups.markers || this._gauge().append('g').attr('class', 'markers')

                return this.components.groups.markers
            },
            _valueLabel () {
                this.components.groups.valueLabel = this.components.groups.valueLabel || this._gauge().append('g').attr('class', 'value-label')

                return this.components.groups.valueLabel
            },
            calculateGeometry (geometry) {
                geometry = geometry || {}
                geometry.width = geometry.width || 250
                geometry.padding = geometry.padding || {}
                geometry.padding.top = geometry.padding.top || 0
                geometry.padding.right = geometry.padding.right || 0
                geometry.padding.bottom = geometry.padding.bottom || 0
                geometry.padding.left = geometry.padding.left || 0
                geometry.dialWidth = geometry.width - geometry.padding.left - geometry.padding.right

                geometry.radius = geometry.dialWidth / 2
                geometry.height = geometry.radius + geometry.padding.top + geometry.padding.bottom

                geometry.colorWidth = geometry.colorWidth || geometry.radius / 4
                geometry.innerRadius = geometry.radius - geometry.colorWidth

                return geometry
            },
            calculateGeometryFromElement (element) {
                let width = element.node().offsetWidth
                this.geometry = this.calculateGeometry({
                    width: width,
                    padding: {
                        top: Math.floor(width * 0.1),
                        right: Math.floor(width * 0.1),
                        bottom: Math.floor(width * 0.1),
                        left: Math.floor(width * 0.1)
                    }
                })
            },
            scaleToRotation (radians) {
                return Angles.degrees(radians) + 90
            },
            rotationToScale (degrees) {
                return Angles.radians(degrees - 90)
            },
            fillStartValues (regions) {
                for (let region in regions) {
                    if (regions[region].start === undefined) {
                        regions[region].start = regions[region - 1] !== undefined && regions[region - 1].end !== undefined ? regions[region - 1].end : 0
                    }
                }
            },
            generateNormalizedScaleRange (scale, range) {
                var total = scale.reduce(function (p, c) { return p + (c.grow || 1) }, 0)
                var unit = range / total

                var output = [this.rotationToScale(0)]
                var current = 0

                for (var element of scale) {
                    current = current + (element.grow || 1)
                    output.push(this.rotationToScale(current * unit))
                }

                return output
            },
            drawGauge () {
                if (this.content === undefined || this.content === null) {
                    return
                }

                this.calculateGeometryFromElement(this._container())
                const gauge = this._gauge(true)
                this.appendGradient(gauge)
                this.drawGaugeStageFill(gauge)
                this.drawAxis(this._axis(), this.scaleSegments)
                this.drawTicks(this._ticks())
                this.drawAxisLabels(this._axisLabels())
                this.drawShadedZones(this._shadedZones(), this.shadedZones)
                this.drawGaugeStageStroke(gauge)
                this.drawMarkers(this._markers(), this.markers)
                this.drawNeedle(gauge, this.geometry)
                this.drawValueLabel(this._valueLabel())
                this.drawGaugeLabel(gauge)
            },
            appendGradient (parent) {
                const defs = parent.append('defs')

                this.addGradient(defs, 'linearGradient', [
                    {offset: '10%', color: '#FF6666'},
                    {offset: '50%', color: '#FF9999'},
                    {offset: '50%', color: '#CC0000'},
                    {offset: '90%', color: '#CC6666'}
                ], {id: 'needleGradient'})
            },
            arcPath (start, end, outerRadius, innerRadius) {
                return this.d3.arc().innerRadius(innerRadius || 0).outerRadius(outerRadius).startAngle(start).endAngle(end)
            },
            drawGaugeStageFill (parent) {
                parent.append('path').attrs({
                    class: 'stage',
                    fill: this.colors.backgroundFill,
                    d: this.arcPath(this.d3.min(this.range), this.d3.max(this.range), this.geometry.radius)
                })
            },
            drawGaugeStageStroke (parent) {
                parent.append('path').attrs({
                    class: 'stage',
                    stroke: this.colors.stroke,
                    fill: 'none',
                    d: this.arcPath(this.d3.min(this.range), this.d3.max(this.range), this.geometry.radius)
                })
                parent.append('path').attrs({
                    class: 'stage',
                    stroke: this.colors.stroke,
                    fill: 'none',
                    d: this.arcPath(this.d3.min(this.range), this.d3.max(this.range), this.geometry.innerRadius)
                })
            },
            drawNeedle (parent, geometry) {
                parent.append('path')
                    .attrs({
                        fill: 'url(#needleGradient)',
                        d: ['M-2,0',
                            -(geometry.radius - (geometry.colorWidth / 2)) / 3 + ',' + Math.max(geometry.colorWidth / 6, 5),
                            -(geometry.radius - 1) + ',0',
                            -(geometry.radius - (geometry.colorWidth / 2)) / 3 + ',' + Math.min(-geometry.colorWidth / 6, -5),
                            '-2,0'
                        ].join(' '),
                        stroke: this.colors.stroke,
                        'stroke-width': '1px',
                        'stroke-alignment': 'outer'
                    })
                    .transition()
                    .attr('transform', 'rotate(' + this.scaleToRotation(this._scale()(this.value)) + ')')
            },
            drawAxis (parent, data) {
                var segments = parent.selectAll('path')
                    .data(data)

                segments.enter()
                    .append('path')
                    .attrs({
                        'class': 'arc',
                        fill: (d) => { return this._colorScale()(d.start) },
                        d: this.d3.arc()
                            .innerRadius(this.geometry.innerRadius)
                            .outerRadius(this.geometry.radius)
                            .startAngle((d) => { return this._scale()(d.start) })
                            .endAngle((d) => { return this._scale()(d.end) })
                    })

                segments.exit().remove()
            },
            drawTicks (parent) {
                this.ticksPerSegment = this.content.ticksPerSegment || Math.floor((this.geometry.width * this.scaleExponent) / 20)

                for (var subScale of this.subScales) {
                    for (var tick of subScale.ticks(Math.max(this.ticksPerSegment / 4, 5))) {
                        this.appendRadialTick(parent, this.scaleToRotation(this._scale()(tick)), this.geometry.radius, this.geometry.colorWidth, this.colors.stroke)
                    }
                }
            },
            drawAxisLabels (parent) {
                let lastPosition = 360
                for (let i = this.domain.length - 1; i >= 0; i--) {
                    var position = this.scaleToRotation(this._scale()(this.domain[i]))
                    if ((lastPosition - position) > 10) {
                        this.drawAxisLabel(parent, this.domain[i], position)
                    }
                    lastPosition = position
                }
            },
            drawAxisLabel (parent, label, rotate) {
                parent.append('g')
                    .attrs({
                        class: 'axis-label',
                        transform: 'rotate(' + rotate + ')'
                    })
                    .append('text')
                    .attrs({
                        fill: this.colors.text,
                        x: (-this.geometry.radius - 10) + 'px',
                        y: Math.max(10 - Math.ceil(this.geometry.radius / 20), 0),
                        transform: 'rotate(-90 ' + (-this.geometry.radius - 10) + ' 0)'
                    })
                    .text(hrNumber(label, 3, 1))
            },
            drawValueLabel (parent) {
                parent.append('text')
                    .attrs({
                        stroke: this.colors.backgroundFill,
                        'stroke-width': '6px',
                        x: '0px',
                        y: '-4px'
                    })
                    .text(hrNumber(this.value, 4, 2))

                parent.append('text')
                    .attrs({
                        fill: this.colors.label,
                        x: '0px',
                        y: '-4px'
                    })
                    .text(hrNumber(this.value, 4, 2))
            },
            drawGaugeLabel (parent) {
                if (this.label) {
                    const gaugeLabel = parent.append('text')
                        .attrs({
                            class: 'gauge-label',
                            fill: this.colors.text,
                            x: '0px',
                            y: '1em'
                        })
                        .text(this.label)

                    let maxWidth = this.geometry.innerRadius * 2 + this.geometry.colorWidth

                    if (gaugeLabel.node().getComputedTextLength() > maxWidth) {
                        let guess
                        let min = 0
                        let max = this.label.length - 1

                        while (min < max - 1) {
                            guess = Math.floor((min + max) / 2)

                            if (gaugeLabel.node().getSubStringLength(0, guess) > maxWidth) {
                                max = guess
                            } else {
                                min = guess
                            }
                        }

                        gaugeLabel.text(this.label.substr(0, guess).trim() + '...')
                    }
                }
            },
            drawShadedZones (parent, data) {
                let zones = parent.selectAll('path')
                    .data(data)

                const self = this

                zones.enter()
                    .append('path')
                    .attrs({
                        'class': (d) => { return d.link !== undefined ? 'wedge clickable' : 'wedge' },
                        'stroke-alignment': 'inner',
                        fill: (d) => { return d.fill || this.lightenColor(this._colorScale()(d.end)) },
                        d: this.d3.arc()
                            .innerRadius(0)
                            .outerRadius(this.geometry.innerRadius)
                            .startAngle((d) => { return this._scale()(d.start) })
                            .endAngle((d) => { return this._scale()(d.end) })
                    })
                    .on('click', function (d) {
                        if (d.link !== undefined) {
                            window.location = d.link
                            self.d3.event.stopPropagation()
                        }
                    })
                    .on('mouseover', function (d) {
                        if (d.link !== undefined) {
                            d3.select(this).attrs({
                                'stroke': self.colors.stroke,
                                'stroke-width': '3px'
                            })
                        }
                    })
                    .on('mouseout', function (d) {
                        if (d.link !== undefined) {
                            d3.select(this).attrs({
                                'stroke': 'none',
                                'stroke-width': '0'
                            })
                        }
                    })

                zones.exit().remove()
            },
            drawMarkers (parent, data) {
                var markers = parent.selectAll('circle')
                    .data(data)

                const self = this

                markers.enter()
                    .append('circle')
                    .attrs({
                        class: (d) => { return d.link !== undefined ? 'marker clickable' : 'marker' },
                        cx: -(this.geometry.innerRadius),
                        cy: 0,
                        r: Math.ceil(this.geometry.colorWidth / 7),
                        fill: 'gold',
                        stroke: '#666666',
                        transform: (d) => { return 'rotate(' + this.scaleToRotation(this._scale()(d.value)) + ')' }
                    })
                    .on('click', function (d) {
                        if (d.link !== undefined) {
                            window.location = d.link
                            self.d3.event.stopPropagation()
                        }
                    })
                    .on('mouseover', function (d) {
                        if (d.link !== undefined) {
                            d3.select(this).attrs({
                                'stroke-width': '2px'
                            })
                        }
                        self._tooltip().transition()
                            .duration(200)
                            .style('opacity', 0.9)

                        let coords = self.d3.mouse(self._container().node())

                        self._tooltip().html(d.label + '<br/>' + hrNumber(d.value, 6, 3))
                            .style('left', coords[0] + 'px')
                            .style('top', coords[1] + 'px')
                    })
                    .on('mouseout', function (d) {
                        if (d.link !== undefined) {
                            d3.select(this).attrs({
                                'stroke-width': '1px'
                            })
                        }
                        self._tooltip().transition()
                            .duration(500)
                            .style('opacity', 0)
                    })

                markers.exit().remove()
            },
            lightenColor (color) {
                color = this.d3.rgb(color)
                color.opacity = 0.3

                return color
            },
            generateScales () {
                this.components.scale = this.d3.scalePow().exponent(this.scaleExponent)
                this.components.colorScale = this.d3.scaleThreshold().domain(this.scaleSegments.map((i) => i.end)).range(this.scaleSegments.map((i) => i.fill))

                this.subScales = []

                if (this.normalizeScale) {
                    this.components.scale.domain(this.domain).range(this.range)

                    for (var i = 0; i < this.domain.length - 1; i++) {
                        this.subScales.push(this.d3.scalePow().exponent(this.scaleExponent).domain(this.domain.slice(i, i + 2)).range(this.range.slice(i, i + 2)))
                    }
                } else {
                    this.components.scale
                        .domain(this.d3.extent(this.domain))
                        .range(this.d3.extent(this.range))

                    this.subScales.push(this.components.scale)
                }
            },
            init (content) {
                this.content = content
                this.value = content.value || 0
                this.colors = Vue.util.extend(this.colors, content.colors || {})
                this.normalizeScale = content.normalizeScale !== undefined ? content.normalizeScale : this.normalizeScale
                this.scaleExponent = content.scaleExponent !== undefined ? content.scaleExponent : this.scaleExponent

                this.scaleSegments = content.scaleSegments || this.scaleSegments
                this.range = this.generateNormalizedScaleRange(this.scaleSegments, 180)
                this.fillStartValues(this.scaleSegments)
                this.domain = [this.d3.min(this.scaleSegments.map((i) => i.start))].concat(this.scaleSegments.map((i) => i.end))

                this.generateScales()

                this.shadedZones = content.shadedZones || []
                this.fillStartValues(this.shadedZones)
                this.markers = content.markers || []

                this.$nextTick(() => {
                    this.drawGauge()
                })
            }
        },
        watch: {
            content () {
                if (this.hasContent) {
                    this.init(this.content)
                }
            }
        },
        created () {
            if (this.hasContent) {
                this.init(this.content)
            }

            let gauge = this
            window.addEventListener('resize', function () {
                gauge.drawGauge()
            })
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>

</style>



// WEBPACK FOOTER //
// Gauge.vue?f79fa7f2